import {
  FileData,
  PsychLabActivity,
  PsychLabUser,
  PsychologicalLab,
} from "../../../api/psychologicalLabApi";
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchActivityList,
  fetchSelectedPsychLabData,
  exportStats,
  fetchStatsFiles,
  downloadStatsFile,
  deleteStatsFile,
  fetchUsersList,
  fetchPsychLabParameters,
  fetchAvailableParameterOptions,
  bulkUpdateParameters,
  PsychLabParameter,
  AvailableParameter,
} from "../../actions/adminPanel/selectedPsychLabActions";
import { BaseApiState } from "../../../api/axiosConfig";

export interface SelectedPsychLabState extends BaseApiState {
  selectedPsychLabId?: number;
  selectedPsychLab?: PsychologicalLab;
  refreshNeeded: boolean;
  loadingPsychLabData: boolean;
  loadingPsychUsers: boolean;
  loadingPsychActivities: boolean;
  loadingExportStats: boolean;
  succeededExportStats: boolean;
  loadingStatsFiles: boolean;
  loadingDeleteStatsFile: boolean;
  succeededDeletedStatsFile: boolean;
  statsFiles: FileData[] | null;
  loadingParameters: boolean;
  loadingAvailableOptions: boolean;
  savingParameters: boolean;
  users: PsychLabUser[];
  activities: PsychLabActivity[];
  parameters: PsychLabParameter[];
  availableParameters: AvailableParameter[];
}

const selectedPsychLabInitialState: SelectedPsychLabState = {
  selectedPsychLabId: undefined,
  selectedPsychLab: undefined,
  refreshNeeded: false,
  succeeded: false,
  loading: false,
  loadingPsychLabData: false,
  error: false,
  errorMessage: undefined,
  errorDetails: undefined,
  users: [],
  activities: [],
  loadingPsychUsers: false,
  loadingPsychActivities: false,
  loadingExportStats: false,
  succeededExportStats: false,
  loadingStatsFiles: false,
  loadingDeleteStatsFile: false,
  succeededDeletedStatsFile: false,
  statsFiles: null,
  loadingParameters: false,
  loadingAvailableOptions: false,
  savingParameters: false,
  parameters: [],
  availableParameters: [],
};

const SelectedPsychLabSlice = createSlice({
  name: "selectedPsychLab",
  initialState: selectedPsychLabInitialState,
  reducers: {
    setSelectedPsychLabId(state, action) {
      state.selectedPsychLabId = action.payload;
    },
    clearActivities(state) {
      state.activities = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSelectedPsychLabData.pending, (state) => {
        state.loadingPsychLabData = true;
      })
      .addCase(fetchSelectedPsychLabData.fulfilled, (state, action) => {
        state.selectedPsychLab = action.payload;
        state.loadingPsychLabData = false;
        state.succeeded = true;
        state.refreshNeeded = false;
      })
      .addCase(fetchSelectedPsychLabData.rejected, (state) => {
        state.loadingPsychLabData = false;
        state.error = true;
        state.errorMessage = undefined;
        state.errorDetails = undefined;
      })
      .addCase(fetchUsersList.pending, (state) => {
        state.loadingPsychUsers = true;
      })
      .addCase(fetchUsersList.rejected, (state) => {
        state.loadingPsychUsers = false;
        state.error = true;
        state.errorMessage = undefined;
        state.errorDetails = undefined;
      })
      .addCase(fetchUsersList.fulfilled, (state, action) => {
        state.users = action.payload;
        state.loadingPsychUsers = false;
        state.succeeded = true;
        state.refreshNeeded = false;
      })
      .addCase(fetchActivityList.pending, (state) => {
        state.loadingPsychActivities = true;
      })
      .addCase(fetchActivityList.rejected, (state) => {
        state.loadingPsychActivities = false;
        state.error = true;
        state.errorMessage = undefined;
        state.errorDetails = undefined;
      })
      .addCase(fetchActivityList.fulfilled, (state, action) => {
        state.activities = action.payload;
        state.loadingPsychActivities = false;
        state.succeeded = true;
        state.refreshNeeded = false;
      })
      .addCase(exportStats.pending, (state) => {
        state.loadingExportStats = true;
        state.succeededExportStats = false;
      })
      .addCase(exportStats.rejected, (state) => {
        state.loadingExportStats = false;
        state.error = true;
        state.errorMessage = undefined;
        state.errorDetails = undefined;
      })
      .addCase(exportStats.fulfilled, (state) => {
        state.loadingExportStats = false;
        state.succeededExportStats = true;
        state.refreshNeeded = true;
      })
      .addCase(fetchStatsFiles.pending, (state) => {
        state.loadingStatsFiles = true;
      })
      .addCase(fetchStatsFiles.rejected, (state) => {
        state.loadingStatsFiles = false;
        state.error = true;
      })
      .addCase(fetchStatsFiles.fulfilled, (state, action) => {
        state.statsFiles = action.payload;
        state.loadingStatsFiles = false;
        state.refreshNeeded = false;
      })
      .addCase(downloadStatsFile.pending, (state) => {
        state.loading = true;
      })
      .addCase(downloadStatsFile.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(downloadStatsFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteStatsFile.pending, (state) => {
        state.loadingDeleteStatsFile = true;
        state.succeededDeletedStatsFile = false;
      })
      .addCase(deleteStatsFile.rejected, (state) => {
        state.loadingDeleteStatsFile = false;
        state.error = true;
      })
      .addCase(deleteStatsFile.fulfilled, (state, action) => {
        state.loadingDeleteStatsFile = false;
        state.succeededDeletedStatsFile = true;

        if (state.statsFiles) {
          state.statsFiles = state.statsFiles.filter(
            (file) => file.name !== action.payload
          );
        }
      })
      // Parameters actions
      .addCase(fetchPsychLabParameters.pending, (state) => {
        state.loadingParameters = true;
      })
      .addCase(fetchPsychLabParameters.rejected, (state) => {
        state.loadingParameters = false;
        state.error = true;
      })
      .addCase(fetchPsychLabParameters.fulfilled, (state, action) => {
        state.parameters = action.payload;
        state.loadingParameters = false;
        state.succeeded = true;
      })
      .addCase(fetchAvailableParameterOptions.pending, (state) => {
        state.loadingAvailableOptions = true;
      })
      .addCase(fetchAvailableParameterOptions.rejected, (state) => {
        state.loadingAvailableOptions = false;
        state.error = true;
      })
      .addCase(fetchAvailableParameterOptions.fulfilled, (state, action) => {
        // Custom renderer owrapuje response w {results: {...}}
        const data = (action.payload as any).results || action.payload;

        state.availableParameters =
          data.availableParameters || data.available_parameters || [];
        state.loadingAvailableOptions = false;
        state.succeeded = true;
      })
      .addCase(bulkUpdateParameters.pending, (state) => {
        state.savingParameters = true;
      })
      .addCase(bulkUpdateParameters.rejected, (state) => {
        state.savingParameters = false;
        state.error = true;
      })
      .addCase(bulkUpdateParameters.fulfilled, (state) => {
        state.savingParameters = false;
        state.succeeded = true;
      });
  },
});

export const selectedPsychLabReducer = SelectedPsychLabSlice.reducer;
export const { setSelectedPsychLabId, clearActivities } =
  SelectedPsychLabSlice.actions;